"use client";

// Statischer Flow mit pulsierendem Trigger.
// Alle Nodes + Edges sind durchgehend sichtbar. Der Puls startet beim Trigger und läuft
// durch die Kette — jeder Node leuchtet kurz auf, wenn der Puls ankommt. Loop 6 Sekunden.

import { useEffect, useRef, useState } from "react";
import { Bot, Mic, Radio, MessageSquare } from "lucide-react";

type Pos = { x: number; y: number };

type FlowNode = {
  id: string;
  label: string;
  title: string;
  sub: string;
  agent: string;
  color: string;
  iconBg: string;
  icon: typeof Bot;
  pos: Pos;
};

const NODES: FlowNode[] = [
  {
    id: "research",
    label: "AGENT",
    title: "Recherche",
    sub: "Sammlung & Analyse",
    agent: "Morgenmagazin Research",
    color: "#1E3AFF",
    iconBg: "#EDEEFF",
    icon: Bot,
    pos: { x: 72, y: 14 },
  },
  {
    id: "moderation",
    label: "AGENT",
    title: "Moderation",
    sub: "OnAir Script",
    agent: "Sport-Moderations-Agent",
    color: "#10B981",
    iconBg: "#E6FAF2",
    icon: Mic,
    pos: { x: 87, y: 32 },
  },
  {
    id: "playout",
    label: "AGENT",
    title: "Playout",
    sub: "Sendesystem Export",
    agent: "DABiS800 Playout Agent",
    color: "#EC4899",
    iconBg: "#FDE7F3",
    icon: Radio,
    pos: { x: 78, y: 58 },
  },
  {
    id: "social",
    label: "AGENT",
    title: "Social Media",
    sub: "Insta & LinkedIn",
    agent: "Social Media Manager",
    color: "#E86A2C",
    iconBg: "#FDEDE2",
    icon: MessageSquare,
    pos: { x: 94, y: 80 },
  },
];

const EDGES: { from: string; to: string; color: string }[] = [
  { from: "research",   to: "moderation", color: "#10B981" },
  { from: "moderation", to: "playout",    color: "#EC4899" },
  { from: "playout",    to: "social",     color: "#E86A2C" },
];

// Pulse-Loop: jeder Node leuchtet der Reihe nach.
// Trigger bei 0s, dann alle ~1.2s der nächste Node.
const PULSE_TIMING = {
  offsets: [0, 1.2, 2.4, 3.6],   // wann ein Node aktiviert wird
  nodeActiveDuration: 0.8,       // wie lange der „Pulse" auf einem Node bleibt
  loopLength: 6,                 // gesamte Schleife
};

// Drift-Parameter pro Node — unterschiedliche Amplituden/Perioden/Phasen,
// damit die Cards asynchron „atmen". Amplituden in Prozent der Container-Größe.
const DRIFT = [
  { ax: 0.9, ay: 1.4, px: 11.0, py:  9.0, phx: 0,   phy: 1.1 },
  { ax: 1.2, ay: 1.0, px:  9.5, py: 12.0, phx: 1.8, phy: 0.4 },
  { ax: 1.0, ay: 1.5, px: 10.5, py: 10.5, phx: 0.7, phy: 2.2 },
  { ax: 1.3, ay: 1.1, px: 12.0, py:  8.5, phx: 2.5, phy: 1.5 },
];

// Nudge-Parameter — wie stark zieht die Maus Cards an, wie weit reicht der Einfluss.
const NUDGE_RADIUS = 45;      // in %-Einheiten des Containers (0-100 Viewbox)
const NUDGE_STRENGTH = 3;     // max. Versatz in %-Einheiten, wenn Maus direkt am Node ist

export function HeroFlowBuilder() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const startRef = useRef<number>(0);
  const [time, setTime] = useState(0);
  const [clock, setClock] = useState(0); // wall-clock in Sekunden für Drift (nicht modulo Loop)
  const [mouse, setMouse] = useState<Pos | null>(null); // Mausposition in %-Container-Koords
  const rafRef = useRef<number>(0);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Nodes intro-faden beim Mount
    setMounted(true);
    startRef.current = performance.now();
    const tick = () => {
      const now = performance.now();
      const secs = (now - startRef.current) / 1000;
      setClock(secs);
      setTime(secs % PULSE_TIMING.loopLength);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  // Maus-Tracking auf Window-Ebene (nicht am Container, damit wir mit
  // pointer-events-none leben können und CTAs darunter klickbar bleiben).
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const el = containerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (
        e.clientX < r.left || e.clientX > r.right ||
        e.clientY < r.top || e.clientY > r.bottom
      ) {
        setMouse(null);
        return;
      }
      setMouse({
        x: ((e.clientX - r.left) / r.width) * 100,
        y: ((e.clientY - r.top) / r.height) * 100,
      });
    };
    const onLeave = () => setMouse(null);
    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("mouseleave", onLeave);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  const reducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

  // Wie stark pulsiert Node i aktuell (0 bis 1)
  const nodePulse = (i: number): number => {
    if (reducedMotion) return 0;
    const t = time - PULSE_TIMING.offsets[i];
    if (t < 0 || t > PULSE_TIMING.nodeActiveDuration) return 0;
    // parabolische Kurve: schnell hoch, langsam runter
    const norm = t / PULSE_TIMING.nodeActiveDuration;
    return Math.sin(norm * Math.PI);
  };

  // Wie stark pulsiert Edge i (0 bis 1) — aktiv während der Puls "wandert"
  // Edge i verbindet Node i zu Node i+1, also aktiv zwischen Node[i].offset und Node[i+1].offset
  const edgePulse = (i: number): number => {
    if (reducedMotion) return 0;
    const startT = PULSE_TIMING.offsets[i];
    const endT = PULSE_TIMING.offsets[i + 1];
    if (time < startT || time > endT) return 0;
    const norm = (time - startT) / (endT - startT);
    return Math.sin(norm * Math.PI);
  };

  // Gedriftete Position — base + sanfte Sinus-Bewegung + optionaler Maus-Nudge.
  // Wird von Nodes UND Edges gelesen, damit die Linien an den Cards kleben bleiben.
  const driftedPos = (index: number): Pos => {
    const base = NODES[index].pos;
    if (reducedMotion) return base;
    const d = DRIFT[index];
    const dx = d.ax * Math.sin((2 * Math.PI * clock) / d.px + d.phx);
    const dy = d.ay * Math.sin((2 * Math.PI * clock) / d.py + d.phy);

    // Nudge: Card wird sanft zum Mauszeiger gezogen, wenn er in der Nähe ist.
    // Falloff quadratisch (smooth), nur aktiv innerhalb NUDGE_RADIUS.
    let nx = 0, ny = 0;
    if (mouse) {
      const vx = mouse.x - (base.x + dx);
      const vy = mouse.y - (base.y + dy);
      const dist = Math.sqrt(vx * vx + vy * vy);
      if (dist < NUDGE_RADIUS && dist > 0.01) {
        const influence = 1 - dist / NUDGE_RADIUS;          // 0..1
        const magnitude = NUDGE_STRENGTH * influence * influence;
        nx = (vx / dist) * magnitude;
        ny = (vy / dist) * magnitude;
      }
    }
    return { x: base.x + dx + nx, y: base.y + dy + ny };
  };
  const nodePos = (id: string) => driftedPos(NODES.findIndex((n) => n.id === id));

  return (
    <div
      ref={containerRef}
      aria-hidden
      className="hidden md:block absolute inset-0 pointer-events-none overflow-hidden"
      style={{
        opacity: mounted ? 0.65 : 0,
        transition: "opacity 800ms ease-out",
      }}
    >
      {/* Gepunkteter Grid-Hintergrund */}
      <div
        className="absolute inset-y-0 right-0 w-[55%]"
        style={{
          backgroundImage: "radial-gradient(rgba(10,10,15,0.08) 1px, transparent 1px)",
          backgroundSize: "20px 20px",
          maskImage: "linear-gradient(to right, transparent 0%, black 20%, black 100%)",
          WebkitMaskImage: "linear-gradient(to right, transparent 0%, black 20%, black 100%)",
        }}
      />

      {/* Edges — stärker sichtbar + zusätzlicher Glow-Pulse beim Durchlauf */}
      <svg
        className="absolute inset-0 w-full h-full"
        preserveAspectRatio="none"
        viewBox="0 0 100 100"
      >
        {EDGES.map((e, i) => {
          const from = nodePos(e.from);
          const to = nodePos(e.to);
          const c1x = (from.x + to.x) / 2;
          const c2x = c1x;
          const d = `M ${from.x} ${from.y} C ${c1x} ${from.y}, ${c2x} ${to.y}, ${to.x} ${to.y}`;
          const pulse = edgePulse(i);
          return (
            <g key={i}>
              {/* Base-Line — dauerhaft sichtbar */}
              <path
                d={d}
                fill="none"
                stroke={e.color}
                strokeWidth="0.45"
                opacity={0.55}
                vectorEffect="non-scaling-stroke"
              />
              {/* Signal-Packet — dashed stroke, wandert kontinuierlich entlang der Edge */}
              <path
                d={d}
                fill="none"
                stroke={e.color}
                strokeWidth="2"
                strokeLinecap="round"
                strokeDasharray="0.6 12"
                opacity={0.9}
                vectorEffect="non-scaling-stroke"
                className={`edge-signal edge-signal-${i}`}
              />
              {/* Glow-Pulse beim Durchlauf des großen Pulses */}
              <path
                d={d}
                fill="none"
                stroke={e.color}
                strokeWidth={1.5}
                opacity={pulse * 0.5}
                vectorEffect="non-scaling-stroke"
                style={{ filter: `blur(2px)` }}
              />
            </g>
          );
        })}
      </svg>

      {/* Styles für Signal-Flow + Node-Floating */}
      <style jsx>{`
        /* Signal-Packets wandern entlang jeder Edge */
        :global(.edge-signal) {
          animation: signal-flow 3s linear infinite;
        }
        :global(.edge-signal-0) { animation-delay: 0s; }
        :global(.edge-signal-1) { animation-delay: -1s; }
        :global(.edge-signal-2) { animation-delay: -2s; }
        @keyframes signal-flow {
          from { stroke-dashoffset: 0; }
          to   { stroke-dashoffset: -12.6; }
        }

        /* Floating-Breath für Nodes — unterschiedliche Delays + Durations */
        .node-float-0 { animation: node-float 5.5s ease-in-out infinite; }
        .node-float-1 { animation: node-float 6.2s ease-in-out infinite; animation-delay: -1.5s; }
        .node-float-2 { animation: node-float 4.8s ease-in-out infinite; animation-delay: -3s; }
        .node-float-3 { animation: node-float 5.9s ease-in-out infinite; animation-delay: -0.8s; }
        @keyframes node-float {
          0%, 100% { transform: translateY(0px); }
          50%      { transform: translateY(-6px); }
        }

        @media (prefers-reduced-motion: reduce) {
          :global(.edge-signal),
          .node-float-0, .node-float-1, .node-float-2, .node-float-3 {
            animation: none !important;
          }
        }
      `}</style>

      {/* Nodes — dauerhaft sichtbar */}
      {NODES.map((n, i) => {
        const Icon = n.icon;
        const pulse = nodePulse(i);
        const p = driftedPos(i);
        return (
          <div
            key={n.id}
            className="absolute"
            style={{
              left: `${p.x}%`,
              top: `${p.y}%`,
              transform: `translate(-50%, -50%)`,
            }}
          >
            <div
              style={{
                transform: `scale(${1 + pulse * 0.04})`,
                transition: "transform 300ms ease-out",
              }}
            >
            {/* Pulsing Glow-Ring */}
            <div
              className="absolute inset-0 rounded-lg pointer-events-none"
              style={{
                boxShadow: `0 0 0 ${6 * pulse}px ${n.color}25, 0 0 ${28 * pulse}px ${
                  12 * pulse
                }px ${n.color}40`,
                transition: "box-shadow 300ms ease-out",
              }}
            />

            {/* Card */}
            <div
              className="relative flex bg-white rounded-lg overflow-hidden"
              style={{
                boxShadow: "0 8px 24px -6px rgba(10,10,15,0.12), 0 0 0 1px rgba(10,10,15,0.06)",
                minWidth: 180,
              }}
            >
              <div style={{ width: 4, background: n.color }} />
              <div className="flex-1 p-2.5">
                <div className="flex items-center gap-2 mb-1.5">
                  <div
                    className="w-6 h-6 rounded-md flex items-center justify-center shrink-0"
                    style={{ background: n.iconBg }}
                  >
                    <Icon size={13} strokeWidth={1.8} style={{ color: n.color }} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[11px] font-semibold leading-tight text-[var(--color-ink)]">
                      {n.title}
                    </div>
                    <div className="text-[9.5px] text-[var(--color-ink-muted)] leading-tight truncate">
                      {n.sub}
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-2 pt-1.5 border-t border-[rgba(10,10,15,0.06)]">
                  <span className="font-mono text-[8px] tracking-[0.14em] uppercase text-[var(--color-ink-muted)]">
                    {n.label}
                  </span>
                  <span
                    className="text-[9.5px] font-medium truncate"
                    style={{ color: n.color }}
                  >
                    {n.agent}
                  </span>
                </div>
              </div>
            </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
